import React from 'react'
import classNames from 'classnames'

import { mq } from 'marketing-site/src/library/utils'
import { IProps } from './index'
import { Text } from 'marketing-site/src/library/elements/Text'
import { RichText } from 'marketing-site/src/mvp/library/elements/RichText'

const getSpacing = (titleSize: IProps['titleSize']) => {
  switch (titleSize) {
    case 'heading-1--mega':
      return {
        mobile: {
          eyebrowTitle: '1.25rem',
          titleSubhead: '1.5rem',
        },
        tablet: {
          eyebrowTitle: '1.5rem',
          titleSubhead: '2rem',
        },
      }
    case 'heading-1':
      return {
        mobile: {
          eyebrowTitle: '1.25rem',
          titleSubhead: '1.5rem',
        },
        tablet: {
          eyebrowTitle: '1.5rem',
          titleSubhead: '2rem',
        },
      }
    case 'heading-1--small':
      return {
        mobile: {
          eyebrowTitle: '1.25rem',
          titleSubhead: '1.5rem',
        },
        tablet: {
          eyebrowTitle: '1.5rem',
          titleSubhead: '1.5rem',
        },
      }
    case 'heading-2':
    default:
      return {
        mobile: {
          eyebrowTitle: '1rem',
          titleSubhead: '1.5rem',
        },
        tablet: {
          eyebrowTitle: '1.5rem',
          titleSubhead: '1.5rem',
        },
      }
    case 'heading-3':
      return {
        mobile: {
          eyebrowTitle: '1rem',
          titleSubhead: '1rem',
        },
        tablet: {
          eyebrowTitle: '1.25rem',
          titleSubhead: '1rem',
        },
      }
  }
}

// TODO: Remove this approach once the MVP V2 <Text /> sizes are implemented.
const getEyebrowProps = (titleSize: IProps['titleSize']) => {
  switch (titleSize) {
    case 'heading-1--mega':
    case 'heading-1':
    case 'heading-1--small':
      return {
        mobile: {
          height: '1.25rem',
          padding: '0 0.5rem',
          fontSize: '0.75rem',
          letterSpacing: '0.125rem',
        },
        tablet: {
          height: '2rem',
          padding: '0 1rem',
          fontSize: '1.25rem',
          letterSpacing: '0.25rem',
        },
      }
    case 'heading-2':
    case 'heading-3':
    default:
      return {
        mobile: {
          height: '1.25rem',
          padding: '0 0.5rem',
          fontSize: '0.75rem',
          letterSpacing: '0.125rem',
        },
        tablet: {
          height: '1.5rem',
          padding: '0 0.625rem',
          fontSize: '1rem',
          letterSpacing: '0.1875rem',
        },
      }
  }
}

export const HeadingBase: React.FC<IProps> = ({
  theme = 'none',
  width = 'default',
  alignment = 'center',
  icon,
  eyebrow,
  title,
  titleSize = 'heading-2',
  subhead,
  subheadSize,
  listStyle,
  ctas,
  brand,
}) => {
  const eyebrowProps = getEyebrowProps(titleSize)

  const classes = classNames('heading-base', {
    [`heading-base--align-${alignment}`]: alignment,
    [`heading-base--width-${width}`]: width,
    [`heading-base--theme-${theme}`]: theme !== 'none',
  })

  return (
    <>
      <div className={classes} data-testid="heading-base">
        {icon && <div className="heading-base__icon">{icon}</div>}
        {eyebrow && (
          <div className="heading-base__eyebrow">
            <Text brand={brand} size="eyebrow">
              {eyebrow}
            </Text>
          </div>
        )}
        {title && (
          <h2 className="heading-base__title">
            <Text brand={brand} size={titleSize}>
              {title}
            </Text>
          </h2>
        )}
        {subhead && (
          <div className="heading-base__subhead">
            <Text
              brand={brand}
              size={
                subheadSize ? subheadSize : titleSize === 'heading-3' ? 'subhead-sm' : 'subhead-lg'
              }
            >
              {typeof subhead === 'string' ? (
                subhead
              ) : (
                <RichText document={subhead} listStyle={listStyle} />
              )}
            </Text>
          </div>
        )}
        {ctas && <div className="heading-base__ctas">{ctas}</div>}
      </div>

      <style jsx>
        {`
          .heading-base {
            grid-column: 1/-1;
            max-width: 77.5rem;
            margin: 0 auto;
            display: flex;
            flex-direction: column;

            &__icon {
              display: flex;
              margin-bottom: 1.25rem;

              :global(picture),
              :global(img) {
                height: 2.5rem;
              }
            }

            &__eyebrow {
              display: inline-flex;

              :global(span) {
                font-size: ${eyebrowProps.mobile.fontSize};
                color: inherit;
                letter-spacing: ${eyebrowProps.mobile.letterSpacing};
              }
            }

            &__eyebrow + &__title {
              margin-top: ${getSpacing(titleSize).mobile.eyebrowTitle};
            }

            &__title + &__subhead {
              margin-top: ${getSpacing(titleSize).mobile.titleSubhead};
            }

            * + &__ctas {
              margin-top: 2.25rem;
            }

            &__ctas {
              display: flex;
              flex-direction: column;

              & > :global(.cta-link + .cta-link) {
                margin-top: 0.5rem;
              }

              & > :global(.cta-link + [class*='cta-link--link-only']) {
                margin-top: 1.375rem;
              }
            }

            &--align-start {
              align-items: start;
              text-align: left;

              .heading-base__icon {
                justify-content: start;
              }
            }

            &--align-center {
              align-items: center;
              text-align: center;

              .heading-base__icon {
                justify-content: center;
              }
            }

            &[class*='theme'] {
              .heading-base__eyebrow {
                height: ${eyebrowProps.mobile.height};
                padding: ${eyebrowProps.mobile.padding};
                align-items: center;
                border-radius: 1rem;
              }
            }

            &--theme-blue-gradient {
              .heading-base__eyebrow {
                color: #003294;
                background: linear-gradient(94deg, #c4e0fd 0%, #5eadff 100%);
              }
            }

            &--theme-aqua-gradient {
              .heading-base__eyebrow {
                color: #093e48;
                background: linear-gradient(94deg, #caf0f8 0%, #87dced 100%);
              }
            }

            &--theme-marigold-gradient {
              .heading-base__eyebrow {
                color: #423200;
                background: linear-gradient(94deg, #fff1d9 -20%, #fbd482 100%);
              }
            }

            &--theme-clay-gradient {
              .heading-base__eyebrow {
                color: #5c1e00;
                background: linear-gradient(94deg, #fae3da -20%, #ec9069 100%);
              }
            }

            &--theme-rainbow-gradient {
              .heading-base__eyebrow {
                color: var(--mvp-slate);
                background: linear-gradient(
                  92deg,
                  #c4e0fd 0%,
                  #87dced 25%,
                  #fbd482 70%,
                  #ec9069 100%
                );
              }
            }
          }

          @media (${mq.tablet}) {
            .heading-base {
              grid-column: 2/-2;

              &__icon {
                margin-bottom: 1.5rem;

                :global(picture),
                :global(img) {
                  height: 3rem;
                }
              }

              &__eyebrow {
                :global(span) {
                  font-size: ${eyebrowProps.tablet.fontSize};
                  letter-spacing: ${eyebrowProps.tablet.letterSpacing};
                }
              }

              &__eyebrow + &__title {
                margin-top: ${getSpacing(titleSize).tablet.eyebrowTitle};
              }

              &__title + &__subhead {
                margin-top: ${getSpacing(titleSize).tablet.titleSubhead};
              }

              * + &__ctas {
                margin-top: 2.5rem;
              }

              &__ctas {
                flex-direction: row;
                align-items: center;

                & > :global(.cta-link + .cta-link) {
                  margin-top: 0rem;
                  margin-left: 1.5rem;
                }
              }

              &[class*='theme'] {
                .heading-base__eyebrow {
                  height: ${eyebrowProps.tablet.height};
                  padding: ${eyebrowProps.tablet.padding};
                }
              }
            }
          }

          @media (${mq.desktop}) {
            .heading-base {
              &--width-narrow {
                grid-column: 4/-4;
              }

              &--width-default {
                grid-column: 3/-3;
              }

              &--width-wide {
                grid-column: 2/-2;
              }
            }
          }

          @media (${mq.desktop}) {
            .heading-base {
              &--width-narrow {
                grid-column: 4/-4;
              }

              &--width-default {
                grid-column: 3/-3;
              }

              &--width-wide {
                grid-column: 2/-2;
              }
            }
          }
        `}
      </style>
    </>
  )
}
